// The envelope every encrypted note, highlight, tag and blank answer is stored
// as (spec section 6.3; API contract section 2): `{ v, kid, iv, ct }`,
// AES-256-GCM under a per-item key derived from MK, authenticated against
// "<memberId>:<type>:<itemId>" so a row cannot be moved between members, types
// or ids without failing to open. Pure functions; no Vue, no store.
import { base64ToBytes, bytesToBase64, utf8Decode, utf8Encode } from './encoding';
import { KEY_ID_PATTERN, assertKeyId, assertMemberId } from './identifiers';
import { assertMasterKey } from './master-key';
import { getSubtle, hkdfKey, randomBytes } from './webcrypto';

export const ENVELOPE_VERSION = 1;
/** The API's `ENVELOPE_MAX_BYTES`: a larger serialised payload is a 400. */
export const ENVELOPE_MAX_BYTES = 65536;
/** Plaintext is padded to a multiple of this so ciphertext length reveals little. */
export const PAD_BLOCK_BYTES = 256;
export const IV_BYTES = 12;
export const ENVELOPE_TYPES = Object.freeze(['note', 'highlight', 'tag', 'blank']);

export const ENVELOPE_ERRORS = Object.freeze({
  /** `v` is newer than this build understands: show "update the app", never garbage. */
  UNKNOWN_VERSION: 'UNKNOWN_VERSION',
  /** `kid` names another MK generation: a rotation happened or is pending. */
  UNKNOWN_KEY: 'UNKNOWN_KEY',
  /** The GCM tag did not verify: wrong key, wrong AAD or tampered ciphertext. */
  AUTH_FAILED: 'AUTH_FAILED',
  /** Not an envelope at all, or not one this writer could have produced. */
  MALFORMED: 'MALFORMED',
  /** The serialised envelope would exceed ENVELOPE_MAX_BYTES. */
  TOO_LARGE: 'TOO_LARGE',
});

const ERROR_MESSAGES = Object.freeze({
  UNKNOWN_VERSION: 'This item was written by a newer app version',
  UNKNOWN_KEY: 'This item was written under another key generation',
  AUTH_FAILED: 'This item could not be authenticated',
  MALFORMED: 'This item is not a valid envelope',
  TOO_LARGE: `This item exceeds ${ENVELOPE_MAX_BYTES} bytes once sealed`,
});

export class EnvelopeError extends Error {
  /**
   * @param {keyof typeof ENVELOPE_ERRORS} code
   * @param {string} [detail]
   */
  constructor(code, detail) {
    super(detail ? `${ERROR_MESSAGES[code]}: ${detail}` : ERROR_MESSAGES[code]);
    this.name = 'EnvelopeError';
    this.code = code;
  }
}

/**
 * @param {unknown} value
 * @returns {value is Record<string, unknown>}
 */
function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * @param {string} value
 * @param {string} name
 */
function assertNonEmptyString(value, name) {
  if (typeof value !== 'string' || value.length === 0) {
    throw new TypeError(`${name} must be a non-empty string`);
  }
}

/**
 * The item id of a blank answer: the same composite the upsert is keyed on.
 *
 * @param {string} programSlug
 * @param {string} lessonSlug
 * @param {string} blockId
 * @returns {string}
 */
export function blankItemId(programSlug, lessonSlug, blockId) {
  assertNonEmptyString(programSlug, 'programSlug');
  assertNonEmptyString(lessonSlug, 'lessonSlug');
  assertNonEmptyString(blockId, 'blockId');
  return `${programSlug}/${lessonSlug}/${blockId}`;
}

/**
 * @param {number} memberId
 * @param {'note' | 'highlight' | 'tag' | 'blank'} type
 * @param {string} itemId the row's UUID, or blankItemId(...) for a blank answer
 * @returns {string} the additional authenticated data bound into the ciphertext
 */
export function buildAad(memberId, type, itemId) {
  assertMemberId(memberId);
  if (!ENVELOPE_TYPES.includes(type)) {
    throw new TypeError(`type must be one of ${ENVELOPE_TYPES.join(', ')}`);
  }
  assertNonEmptyString(itemId, 'itemId');
  return `${memberId}:${type}:${itemId}`;
}

/**
 * Per-item key: HKDF-SHA256(MK, salt = memberId, info = "item/" + itemId).
 * Derived, never stored (spec section 6.1).
 *
 * @param {Uint8Array} mk
 * @param {number} memberId
 * @param {string} itemId
 * @returns {Promise<CryptoKey>}
 */
function deriveItemKey(mk, memberId, itemId) {
  return hkdfKey({
    ikm: mk,
    salt: utf8Encode(String(memberId)),
    info: `item/${itemId}`,
    algorithm: { name: 'AES-GCM', length: 256 },
    usages: ['encrypt', 'decrypt'],
  });
}

/**
 * Serialises the fields and pads the JSON to a multiple of PAD_BLOCK_BYTES with
 * spaces before the closing brace, which JSON.parse ignores, so the padding
 * needs no marker to strip. Only an object literal can be padded that way: a
 * value with `toJSON` (a Date, a dayjs instance) serialises to a string, and
 * splicing a brace onto it would seal something `open` can never parse.
 *
 * @param {Record<string, unknown>} plaintext
 * @returns {Uint8Array}
 */
function serialisePlaintext(plaintext) {
  const json = JSON.stringify(plaintext);
  if (typeof json !== 'string' || !json.startsWith('{') || !json.endsWith('}')) {
    throw new TypeError('plaintext must serialise to a JSON object');
  }
  const bytes = utf8Encode(json);
  const target = Math.ceil(bytes.length / PAD_BLOCK_BYTES) * PAD_BLOCK_BYTES;
  if (target === bytes.length) return bytes;
  return utf8Encode(json.slice(0, -1) + ' '.repeat(target - bytes.length) + '}');
}

/**
 * @param {object} args
 * @param {number} args.memberId
 * @param {'note' | 'highlight' | 'tag' | 'blank'} args.type
 * @param {string} args.itemId
 * @param {Record<string, unknown>} args.plaintext the fields section 6.4 puts inside
 * @param {Uint8Array} args.mk
 * @param {string} args.keyId MK's keyId, stored as `kid`
 * @returns {Promise<{ v: number, kid: string, iv: string, ct: string }>}
 */
export async function seal({ memberId, type, itemId, plaintext, mk, keyId }) {
  assertMasterKey(mk);
  assertKeyId(keyId);
  if (!isPlainObject(plaintext)) throw new TypeError('plaintext must be a plain object');
  const aad = buildAad(memberId, type, itemId);
  const padded = serialisePlaintext(plaintext);
  const key = await deriveItemKey(mk, memberId, itemId);
  const iv = randomBytes(IV_BYTES);
  const ciphertext = await getSubtle().encrypt(
    { name: 'AES-GCM', iv, additionalData: utf8Encode(aad) },
    key,
    padded,
  );
  const envelope = {
    v: ENVELOPE_VERSION,
    kid: keyId,
    iv: bytesToBase64(iv),
    ct: bytesToBase64(new Uint8Array(ciphertext)),
  };
  if (utf8Encode(JSON.stringify(envelope)).length > ENVELOPE_MAX_BYTES) {
    throw new EnvelopeError(ENVELOPE_ERRORS.TOO_LARGE);
  }
  return envelope;
}

/**
 * @param {unknown} envelope
 * @param {string} keyId
 * @returns {{ iv: Uint8Array, ct: Uint8Array }}
 */
function decodeEnvelope(envelope, keyId) {
  if (!isPlainObject(envelope)) throw new EnvelopeError(ENVELOPE_ERRORS.MALFORMED, 'not an object');
  if (!Number.isInteger(envelope.v))
    throw new EnvelopeError(ENVELOPE_ERRORS.MALFORMED, 'no version');
  if (envelope.v !== ENVELOPE_VERSION) throw new EnvelopeError(ENVELOPE_ERRORS.UNKNOWN_VERSION);
  if (typeof envelope.kid !== 'string' || !KEY_ID_PATTERN.test(envelope.kid)) {
    throw new EnvelopeError(ENVELOPE_ERRORS.MALFORMED, 'kid is not a key id');
  }
  if (envelope.kid !== keyId) throw new EnvelopeError(ENVELOPE_ERRORS.UNKNOWN_KEY);
  let iv;
  let ct;
  try {
    iv = base64ToBytes(envelope.iv);
    ct = base64ToBytes(envelope.ct);
  } catch (error) {
    throw new EnvelopeError(ENVELOPE_ERRORS.MALFORMED, 'iv or ct is not base64');
  }
  if (iv.length !== IV_BYTES) throw new EnvelopeError(ENVELOPE_ERRORS.MALFORMED, 'iv length');
  return { iv, ct };
}

/**
 * @param {object} args
 * @param {unknown} args.envelope the stored `payload`
 * @param {number} args.memberId
 * @param {'note' | 'highlight' | 'tag' | 'blank'} args.type
 * @param {string} args.itemId
 * @param {Uint8Array} args.mk
 * @param {string} args.keyId the generation this device holds; an envelope under
 *   another `kid` is UNKNOWN_KEY, which the reader treats as "check for a
 *   rotation", never as tampering
 * @returns {Promise<Record<string, unknown>>} the fields that were sealed
 */
export async function open({ envelope, memberId, type, itemId, mk, keyId }) {
  assertMasterKey(mk);
  assertKeyId(keyId);
  const aad = buildAad(memberId, type, itemId);
  const { iv, ct } = decodeEnvelope(envelope, keyId);
  const key = await deriveItemKey(mk, memberId, itemId);
  let plaintext;
  try {
    plaintext = await getSubtle().decrypt(
      { name: 'AES-GCM', iv, additionalData: utf8Encode(aad) },
      key,
      ct,
    );
  } catch (error) {
    throw new EnvelopeError(ENVELOPE_ERRORS.AUTH_FAILED);
  }
  let fields;
  try {
    fields = JSON.parse(utf8Decode(plaintext));
  } catch (error) {
    throw new EnvelopeError(ENVELOPE_ERRORS.MALFORMED, 'plaintext is not JSON');
  }
  if (!isPlainObject(fields)) throw new EnvelopeError(ENVELOPE_ERRORS.MALFORMED, 'not an object');
  return fields;
}
